import { supabase } from '../lib/supabase';

/**
 * Personal PRD section layout (which sections are on, and their order),
 * stored per user per document type.
 *
 * Deliberately USER-scoped, not org-scoped — unlike every other service here.
 * The preference belongs to the person and follows them across orgs, and the
 * row holds no tenant data. `document_templates` therefore has no `org_id`
 * and its RLS enforces `auth.uid() = user_id` instead of org membership;
 * see sql/document_templates_rls.sql. Do not "fix" this by adding an org
 * filter — there is no column to filter on.
 */

export interface SectionConfig {
    name: string;
    enabled: boolean;
    order: number;
}

export interface DocumentTemplate {
    id: string;
    user_id: string;
    document_type: string;
    sections: SectionConfig[];
    created_at: string;
    updated_at: string;
}

class DocumentTemplateService {
    /**
     * Get saved template for a specific document type
     */
    async getTemplate(userId: string, documentType: string): Promise<SectionConfig[] | null> {
        try {
            const { data, error } = await supabase
                .from('document_templates')
                .select('sections')
                .eq('user_id', userId)
                .eq('document_type', documentType)
                .single();

            if (error) {
                if (error.code === 'PGRST116') {
                    // No template found, return null
                    return null;
                }
                throw error;
            }

            return data?.sections || null;
        } catch (error) {
            console.error('Error fetching document template:', error);
            return null;
        }
    }

    /**
     * Save or update template for a specific document type
     */
    async saveTemplate(
        userId: string,
        documentType: string,
        sections: SectionConfig[]
    ): Promise<boolean> {
        try {
            const { error } = await supabase
                .from('document_templates')
                .upsert(
                    {
                        user_id: userId,
                        document_type: documentType,
                        sections,
                        updated_at: new Date().toISOString(),
                    },
                    {
                        onConflict: 'user_id,document_type',
                    }
                );

            if (error) throw error;
            return true;
        } catch (error) {
            console.error('Error saving document template:', error);
            return false;
        }
    }

    /**
     * Delete template for a specific document type
     */
    async deleteTemplate(userId: string, documentType: string): Promise<boolean> {
        try {
            const { error } = await supabase
                .from('document_templates')
                .delete()
                .eq('user_id', userId)
                .eq('document_type', documentType);

            if (error) throw error;
            return true;
        } catch (error) {
            console.error('Error deleting document template:', error);
            return false;
        }
    }
}

export const documentTemplateService = new DocumentTemplateService();
